import React from 'react';
import moment from 'moment';

import { QueryStringContext } from 'contexts/QueryStringContext';
import { SettingsOptionsContext } from 'contexts/SettingsOptionsContext';
import { SpendCubeSettingsContext } from 'contexts/SpendCubeSettingsContext';

import { convertValueToUnitPair } from 'utils/number';
import { MONTH_NAMES, getMonthByDate, getYearByDate } from 'utils/date';
import {
	getTailSpendAnalysisConfigurationForSave,
	getTailSpendAnalysisConfigurationForProcessing,
} from 'Settings/utils';
import {
	getTailSpendAnalysisData,
	getSettingsOptions,
	getSpendCubeSettings,
} from 'utils/apiRequests';
import { getCurrencySymbolFromOptions } from 'Details/utils';

import { TailSpendPreview } from 'Settings/TailSpendSection/TailSpendPreview';

const defaultSettings = {
	tailSpend: 20,
	tailSpendType: 0,
};

class TailSpendPreviewClass extends React.Component {
	state = {};

	async componentDidMount() {
		await this.fetchTailSpendData();
		await this.fetchPreviewData();
	}

	fetchTailSpendData = () => {
		return Promise.all([getSettingsOptions(), getSpendCubeSettings()]).then(
			([
				options,
				{
					tailSpend,
					othersThreshold,
					spendCubeCurrency,
					spendCubeDateRangeId,
					vendorSpendAmount,
					tailSpendAnalysisConfiguration,
				},
			]) => {
				const tailSpendValue =
					tailSpend.value != null ? tailSpend.value : defaultSettings.tailSpend;
				const tailSpendType =
					tailSpend.type === null
						? defaultSettings.tailSpendType
						: tailSpend.type;
				const customPeriods = options.dateRanges.filter(item => !item.year);
				const periodsData = options.dateRanges.filter(item => !!item.year);
				const useNetSpend = tailSpend.useNetSpend;

				this.setState({
					periodsData,
					tailSpend: tailSpendValue,
					tailSpendType,
					useNetSpend,
					period: spendCubeDateRangeId
						? spendCubeDateRangeId.toString()
						: options.dateRanges &&
						  options.dateRanges[0] &&
						  options.dateRanges[0].id
							? options.dateRanges[0].id.toString()
							: null,
					customPeriods: this.getCustomPeriodsData(customPeriods),
					tailSpendAnalysisConfiguration:
						tailSpendAnalysisConfiguration &&
						getTailSpendAnalysisConfigurationForProcessing(
							tailSpendAnalysisConfiguration
						),
					currency: spendCubeCurrency
						? spendCubeCurrency.toLowerCase()
						: options.currencies[0].code,
					...(othersThreshold &&
						convertValueToUnitPair(othersThreshold, 'category')),
					...(vendorSpendAmount &&
						convertValueToUnitPair(vendorSpendAmount, 'vendorSpend')),
				});
			}
		);
	};

	getCustomPeriodsData = data => {
		return data.map(period => {
			const { dateFrom, dateTo, ...customPeriod } = period;

			return {
				...customPeriod,
				monthPickerRange: this.getMonthPickerRange(dateFrom, dateTo),
			};
		});
	};

	getMonthPickerRange = (dateFrom, dateTo) => {
		return {
			from: {
				year: getYearByDate(dateFrom),
				month: MONTH_NAMES[getMonthByDate(dateFrom)].toLowerCase(),
				monthNumber: getMonthByDate(dateFrom) + 1,
			},
			to: {
				year: getYearByDate(dateTo),
				month: MONTH_NAMES[getMonthByDate(dateTo)].toLowerCase(),
				monthNumber: getMonthByDate(dateTo) + 1,
			},
		};
	};

	fetchPreviewData = () => {
		const { setSegmentDownloadLinkEnabled } = this.props;
		const {
			tailSpend,
			tailSpendType,
			tailSpendAnalysisConfiguration,
			useNetSpend,
		} = this.state;

		const {
			calculationType,
			dateFrom,
			dateTo,
		} = tailSpendAnalysisConfiguration;

		const segments =
			getTailSpendAnalysisConfigurationForSave(tailSpendAnalysisConfiguration)
				.segments || [];

		const tailSpendParams = {
			tailSpendValue: tailSpend,
			tailSpendType,
			dateFrom,
			dateTo,
			calculationType,
			segments,
			useNetSpend,
		};

		return Promise.all([
			getTailSpendAnalysisData({
				...tailSpendParams,
				harmonized: 'true',
			}),
			getTailSpendAnalysisData({
				...tailSpendParams,
				harmonized: 'false',
			}),
		]).then(([harmonized, original]) => {
			this.setState(
				{
					previewData: {
						harmonized: this.getCompletePreviewData(harmonized, segments),
						original: this.getCompletePreviewData(original, segments),
					},
				},
				() => {
					setSegmentDownloadLinkEnabled(
						!!harmonized.segmentsData && harmonized.segmentsData.length > 0
					);
				}
			);
		});
	};

	getCompletePreviewData = (data, segments) => {
		const { segmentsData } = data;

		return {
			...data,
			segmentsData: segments.map(({ name }) => {
				const found = segmentsData.find(item => item.title === name);

				if (found) {
					return found;
				}

				return {
					title: name,
					percentage: 0,
					value: 0,
					vendorsCount: 0,
				};
			}),
		};
	};

	getDateRangeText() {
		const { dateRanges, dateRangeId } = this.props;

		const selectedDateRange =
			dateRanges.find(({ id }) => id === dateRangeId) || dateRanges[0];

		const { dateFrom, dateTo } = selectedDateRange;
		const f = 'MMM YYYY';
		return `${moment(dateFrom).format(f)} – ${moment(dateTo).format(f)}`;
	}

	render() {
		const { currencySymbol } = this.props;
		const { previewData } = this.state;

		return previewData ? (
			<TailSpendPreview
				previewData={previewData}
				currencySymbol={currencySymbol}
				dateRangeText={this.getDateRangeText()}
			/>
		) : null;
	}
}

export const TailSpendPreviewContainer = props => (
	<SettingsOptionsContext.Consumer>
		{({ options }) => (
			<SpendCubeSettingsContext.Consumer>
				{({ settings }) => (
					<QueryStringContext.Consumer>
						{({ unit, currency, showHarmonizedVendors }) => {
							const currencySymbol = getCurrencySymbolFromOptions(
								options.currency,
								currency
							);

							return (
								<TailSpendPreviewClass
									{...props}
									dateRangeId={+settings.spendCubeDateRangeId}
									dateRanges={(options ? options.dateRanges : []) || []}
									scaleSymbol={unit || ''}
									currencySymbol={currencySymbol}
									harmonized={showHarmonizedVendors}
								/>
							);
						}}
					</QueryStringContext.Consumer>
				)}
			</SpendCubeSettingsContext.Consumer>
		)}
	</SettingsOptionsContext.Consumer>
);

export default TailSpendPreviewContainer;
